import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';

/**
 * Item de actividad arrastrable.
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.act - Datos de la actividad
 * @param {number} props.index - Índice actual (para mostrar número de orden y animación escalonada)
 * @param {Function} props.onEditClick - Callback para editar, recibe (actividad)
 * @param {Function} props.onDeleteRequest - Callback para eliminar, recibe (actividad)
 * @param {boolean} [props.canEdit=true] - Si el usuario puede editar actividades
 * @param {boolean} [props.canDelete=true] - Si el usuario puede eliminar actividades
 * @returns {JSX.Element}
 */
function SortableActividad({ act, index, onEditClick, onDeleteRequest, canEdit = true, canDelete = true }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: act.id });
  const [descExpanded, setDescExpanded] = useState(false);

  // Umbral: ~120 chars caben en ~2 líneas en el sidebar (500px - indent - acciones)
  const hasLongDesc = act.descripcion && act.descripcion.length > 120;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    animationDelay: `${index * 0.05}s`,
  };

  return (
    <li ref={setNodeRef} style={style} className={`actividad-item${isDragging ? ' dragging' : ''}`}>
      <div className="actividad-drag-handle" {...listeners} {...attributes} aria-label="Arrastrar para reordenar">
        <i className="fas fa-grip-vertical" aria-hidden="true"></i>
      </div>
      <div className="actividad-content">
        {/* Encabezado: badge + nombre a la izquierda, acciones a la derecha */}
        <div className="actividad-header">
          <div className="actividad-header-main">
            <span className="actividad-orden">{index + 1}</span>
            <span className="actividad-nombre">{act.nombre}</span>
          </div>
          <div className="actividad-actions">
            {canEdit && (
              <button className="btn btn-table-action btn-ghost-info" onClick={() => onEditClick(act)} title="Editar">
                <i className="fas fa-edit"></i>
              </button>
            )}
            {canDelete && (
              <button className="btn btn-table-action btn-ghost-danger" onClick={() => onDeleteRequest(act)} title="Eliminar">
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </div>

        {/* Descripción a ancho completo, sin indentación artificial */}
        {act.descripcion && (
          <>
            <p className={`actividad-descripcion${descExpanded ? ' actividad-descripcion--expanded' : ''}`}>
              {act.descripcion}
            </p>
            {hasLongDesc && (
              <button
                className="actividad-desc-toggle"
                onClick={(e) => { e.stopPropagation(); setDescExpanded(v => !v); }}
                aria-expanded={descExpanded}
              >
                {descExpanded ? 'Ver menos' : 'Ver más'}
              </button>
            )}
          </>
        )}

      </div>
    </li>
  );
}

/**
 * Tarjeta flotante que sigue el cursor durante el arrastre (DragOverlay).
 * Muestra el item levantado con sombra y rotación para dar feedback visual.
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.act - Datos de la actividad siendo arrastrada
 * @param {number} props.index - Posición actual (para mostrar número de orden)
 * @returns {JSX.Element}
 */
function DragCard({ act, index }) {
  return (
    <div className="actividad-drag-card">
      <div className="actividad-drag-handle actividad-drag-handle--active">
        <i className="fas fa-grip-vertical" aria-hidden="true"></i>
      </div>
      <div className="actividad-content">
        <div className="actividad-header">
          <div className="actividad-header-main">
            <span className="actividad-orden">{index + 1}</span>
            <span className="actividad-nombre">{act.nombre}</span>
          </div>
        </div>
        {act.descripcion && <p className="actividad-descripcion">{act.descripcion}</p>}
      </div>
    </div>
  );
}

/**
 * Lista de actividades de una plantilla con drag & drop y CRUD.
 * Patrón: useCotizacionDetalle.js (optimistic update + rollback manual).
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.plantillaId - ID de la plantilla padre
 * @param {Function} props.onAddClick - Callback para abrir modal nueva actividad
 * @param {Function} props.onEditClick - Callback para editar actividad, recibe (actividad)
 * @param {Function} props.onDeleteRequest - Callback para confirmar eliminación, recibe (actividad)
 * @param {number} [props.refreshTrigger=0] - Cambiar valor para forzar recarga
 * @returns {JSX.Element}
 */
function ActividadesPlantillaList({ plantillaId, onAddClick, onEditClick, onDeleteRequest, refreshTrigger = 0 }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('plantillas_mantenimiento.update');
  const canDelete = hasPermission('plantillas_mantenimiento.delete');
  const { showToast } = useToast();
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Ref para evitar stale closure en handleDragEnd (patrón MEMORY.md)
  const actividadesRef = useRef(actividades);
  useEffect(() => { actividadesRef.current = actividades; }, [actividades]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchActividades = useCallback(async () => {
    if (!plantillaId) return;
    setLoading(true);
    try {
      const response = await apiService.getActividadesPlantilla(plantillaId);
      if (response.success) {
        setActividades(response.data || []);
      }
    } catch (error) {
      showToast('Error al cargar actividades', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillaId, showToast, refreshTrigger]);

  useEffect(() => {
    fetchActividades();
  }, [fetchActividades]);

  /**
   * Maneja fin de arrastre: actualiza orden optimísticamente y persiste en API.
   * Revierte el estado local si la API falla.
   * @param {Object} event - Evento de dnd-kit con active y over
   */
  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const current = actividadesRef.current;
    const oldIndex = current.findIndex(a => a.id === active.id);
    const newIndex = current.findIndex(a => a.id === over.id);
    const previous = [...current];
    const reordered = arrayMove(current, oldIndex, newIndex).map((a, i) => ({ ...a, orden: i + 1 }));

    setActividades(reordered);
    setReordering(true);
    try {
      const res = await apiService.reordenarActividadesPlantilla(
        plantillaId, reordered.map(a => ({ id: a.id, orden: a.orden }))
      );
      if (!res.success) { setActividades(previous); showToast('Error al reordenar', 'error'); }
    } catch {
      setActividades(previous);
      showToast('Error al reordenar', 'error');
    } finally {
      setReordering(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillaId]); // showToast y setActividades omitidos — inestables, patrón MEMORY.md

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  // Derivado para el DragOverlay
  const activeActividad = actividades.find(a => a.id === activeId);
  const activeIndex = actividades.findIndex(a => a.id === activeId);

  if (loading) {
    return (
      <div className="actividades-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Cargando actividades...</span>
      </div>
    );
  }

  return (
    <div className="actividades-plantilla-container">
      {/* Área desplazable con la lista */}
      <div className="actividades-scroll-area">
        {actividades.length === 0 ? (
          <div className="actividades-empty">
            <div className="actividades-empty-icon">
              <i className="fas fa-tasks"></i>
            </div>
            <h4>Sin actividades</h4>
            <p>Agregue los pasos del protocolo de mantenimiento para esta plantilla</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={actividades.map(a => a.id)}
              strategy={verticalListSortingStrategy}
              disabled={reordering}
            >
              <ol className="actividades-list">
                {actividades.map((act, index) => (
                  <SortableActividad
                    key={act.id}
                    act={act}
                    index={index}
                    onEditClick={onEditClick}
                    onDeleteRequest={onDeleteRequest}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                ))}
              </ol>
            </SortableContext>

            {/* Tarjeta flotante con sombra que sigue el cursor durante el arrastre */}
            <DragOverlay>
              {activeActividad ? (
                <DragCard act={activeActividad} index={activeIndex} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Footer sticky con botón agregar */}
      {canEdit && (
        <div className="actividades-footer">
          <button className="btn btn-primary" onClick={onAddClick}>
            <i className="fas fa-plus"></i> Agregar Actividad
          </button>
        </div>
      )}
    </div>
  );
}

export default ActividadesPlantillaList;
